/**
 * Briques communes aux widgets catégoriels à drill-down (donut, treemap, carte) :
 * agrégation des lignes par valeur de dimension et tooltip d'item formaté.
 *
 * Les widgets d'axe (line/bar/area) passent par `buildTooltip` (`trigger: 'axis'`)
 * de `chartStyle`. Un donut, un treemap et une carte déclenchent leur tooltip sur
 * l'item : le libellé vient de `params.name`, pas d'un axe. Le style, lui, reste
 * celui de `chartStyle` (`TOOLTIP_STYLE`) — rien n'est redéfini ici.
 */
import { aggregateMetric } from '../aggregate';
import { formatDateLabel } from '../chartFormat';
import { formatMetricValue, type MetricFormat } from '../formatValue';
import { escapeHtml, TOOLTIP_STYLE } from '../chartStyle';
import { metricDef, type FieldCatalog } from '../fieldCatalog';
import type { Row } from '../useWidgetData';
import type { DrillItem } from '../useDrillDown';

/**
 * Agrège les lignes par valeur de dimension, trie décroissant, tronque à `limit`.
 * Les buckets temporels sont formatés au passage (`20260713` est illisible tel quel).
 *
 * L'agrégation de chaque catégorie passe par `aggregateMetric` (`../aggregate`), la
 * seule règle du builder : une métrique additive se somme, un ratio (taux de rebond,
 * CPC, durée moyenne) se RECOMPOSE — somme(_numérateur) / somme(_dénominateur). Une
 * somme brute est atteignable depuis l'UI (granularité jour + dimension pays → 30
 * lignes par pays) et transformerait un taux de rebond de 0,4 en 12, affiché « 1 200 % ».
 *
 * La définition de la métrique est lue dans le catalogue déjà fourni (`metricDef`) :
 * un seul endroit où la résoudre, aucune divergence possible entre appelants.
 */
export function aggregateCategoryItems(
  rows: Row[],
  dimension: string,
  metric: string,
  catalog: FieldCatalog | null,
  limit: number,
): DrillItem[] {
  const isTime = !!catalog?.dimensions.find((d) => d.key === dimension)?.time;
  const def = metricDef(catalog, metric);

  // Les lignes sont regroupées telles quelles : `aggregateMetric` a besoin des
  // composants `_numerator` / `_denominator` de chaque ligne, pas d'un total.
  const groups = new Map<string, Row[]>();
  for (const row of rows) {
    const raw = String(row[dimension] ?? '');
    const key = isTime ? formatDateLabel(raw, dimension) : raw;
    const group = groups.get(key);
    if (group) group.push(row);
    else groups.set(key, [row]);
  }

  return [...groups.entries()]
    .map(([key, group]) => ({
      key,
      value: aggregateMetric(group, metric, def),
      _lat: coordFrom(group, '_lat'),
      _lng: coordFrom(group, '_lng'),
    }))
    .sort((a, b) => b.value - a.value)
    .slice(0, limit);
}

// Coords portées par les rows villes (identiques dans un groupe) ; null si absentes.
function coordFrom(group: Row[], field: '_lat' | '_lng'): number | null {
  for (const row of group) {
    const v = row[field];
    if (v != null) return Number(v);
  }
  return null;
}

/** Un item de tooltip ECharts (`trigger: 'item'`). */
export interface TooltipItemParam {
  name: string;
  /** Une carte à points renvoie `[lng, lat, valeur]`. */
  value: number | number[];
  percent?: number;
}

/** Valeur numérique d'un item, quelle que soit la forme renvoyée par ECharts. */
export function itemValue(param: TooltipItemParam): number {
  const raw = Array.isArray(param.value) ? param.value[2] : param.value;
  return Number(raw) || 0;
}

/** Tooltip d'item : nom de la catégorie + valeur formatée selon le format de la métrique. */
export function buildItemTooltip(format: MetricFormat, withPercent = false) {
  return {
    ...TOOLTIP_STYLE,
    trigger: 'item' as const,
    formatter: (param: TooltipItemParam) => {
      const value = formatMetricValue(itemValue(param), format);
      const head = `<strong>${escapeHtml(String(param.name ?? ''))}</strong>`;
      if (!withPercent) return `${head}<br/>${escapeHtml(value)}`;
      const pct = `${(param.percent ?? 0).toLocaleString('fr-FR', { maximumFractionDigits: 1 })} %`;
      return `${head}<br/>${escapeHtml(value)} (${pct})`;
    },
  };
}
